import Alert from 'jsx/lib/alert';
import _s from 'js/vendor/underscore/string';
import Config from 'js/config';
import React from 'react';
import PropTypes from 'prop-types';
import Posts from 'js/collections/posts';

export default {

  /**
   * Common context types we need throughout BG specific components
   */
  contextTypes: {
    app: PropTypes.any.isRequired,
    page: PropTypes.any,
  },

  /**
   * Before component is inserted into dom
   */
  componentWillMount: function() {
    this.stealBetelgeuseProps(this.props);
  },

  /**
   * When component is about to be given new props
   */
  componentWillReceiveProps: function(props) {
    this.stealBetelgeuseProps(props);
  },

  /**
   * Try and extract certain objects we commonly require
   * @param props
   */
  stealBetelgeuseProps: function(props) {
    // Use our app context
    this.app = props.app || this.context.app;
    if (this.app) {
      this.ctx = this.app.context;
    }
  },

  /**
   * Given a posts collection and optional extra data, generate a page uri string
   * This method will look at desc data first and fallback to collection description
   * @param  {Posts} posts Posts collection
   * @param  {Object} desc  Extra desc data
   * @param  {String} root  Root of this uri (after groupCode) ie. 'posts' by default
   * @return {String}       Uri string
   */
  generatePostsLink: function(posts, desc = {}, root = 'posts') {
    // Ensure posts
    posts = posts || new Posts();

    // Extract data
    const { categoryFilter, pageNumber, groupCode } = Object.assign({}, posts.description || {}, desc);

    // Build uri
    var uri = `/${groupCode}/${root}`;
    if (categoryFilter) {
      uri += `/topic/${encodeURIComponent(categoryFilter).toLowerCase()}`;
    }
    if (pageNumber && pageNumber !== 1) {
      uri += '/page/' + pageNumber;
    }

    return uri;
  },

  /**
   * Given a post model generate a uri string for links
   * @param  {Post} post Post model
   * @param  {String} root Root part of the uri after groupCode. ie. 'posts' by default
   * @param  {String} cachedGroupCode Cached Group Code
   * @return {String}      Uri string
   */
  generatePostLink: function(post, root, cachedGroupCode) {
    const fullUrl = post.get('url') || post.get('linkToPost');
    if (!!fullUrl) {
      return this.getPath(fullUrl);
    }

    let id;
    let responseId;
    let title;
    switch (post.get('postTypeId')) {
      case 2:
      case 19:
        id = post.get('superParentId') || (post.get('superParent') && post.get('superParent').postId);
        responseId = post.id || post.get('postId');
        // @FIXME replace by superParent UrlEncodedTitle
        title = 'p';
        root = post.get('superParentTypeId') === 4 && 'posts' ||
          post.get('superParentTypeId') === 6 && 'polls' ||
          root;
        break;
      case 6:
        root = 'polls';
        id = post.id || post.get('postId');
        title = post.get('urlEncodedTitle') || _s.createSlug(post.get('title'));
        break;
      case 1:
      case 4:
      default:
        root = post.get('isPrivate') ? 'posts/private' : 'posts';
        id = post.id || post.get('postId');
        title = post.get('urlEncodedTitle') || _s.createSlug(post.get('title'));
        break;
    }

    var groupCode = post.get('groupCode');
    if (cachedGroupCode) {
      groupCode = cachedGroupCode;
    } else {
      var cachedGroup = post.getCachedData('groups', post.get('groupId'));
      if (cachedGroup) {
        groupCode = cachedGroup.get('subDomain');
      }
    }

    return '/' + groupCode + '/' + (root || 'posts') + '/' + id + '/' + title +
      (responseId !== undefined ? `?responses=${responseId}` : '');
  },

  /**
   * Given a post model generate a uri string for links
   * @param  {Post} post Post model
   * @param  {String} root Root part of the uri after groupCode. ie. 'posts' by default
   * @return {String}      Uri string
   */
  generateEditPostLink: function(post, root) {
    return '/' + post.get('groupCode') + '/' + (root || 'posts') + '/edit/' + post.id;
  },

  /**
   * Given a searches collection and optional extra data, generate a page uri string
   * This method will look at desc data first and fallback to collection description
   * @param  {Searches} searches Searches collection
   * @param  {Object} desc  Extra desc data
   * @param  {string} root  Root location for search page? Eg. search page or entity - defaults to /search/{query}
   * @return {String}       Uri string
   */
  generateSearchLink: function(communities, desc, root) {

    // Extract data
    var colDesc = communities.description;
    desc = desc || {};
    var start = desc.start || desc.start === 0 ? desc.start : colDesc.start;
    var sort = desc.sortDate || colDesc.sortDate;
    var group = desc.groupCode || colDesc.groupCode;
    var query = encodeURIComponent(desc.query || colDesc.query);
    var lang = desc.lang || colDesc.lang;

    // Build uri
    var uri = root ? '/' + root : '/search/' + query;
    // Only bother any further building if we have a query set
    if (query && group) {
      uri += '/group/' + group;
    }
    if (query && sort) {
      uri += '/sort/date'; // When sort is true we are sorting date (otherwise relevance)
    }
    if (query && start) {
      uri += '/start/' + start;
    }
    if (lang && lang != app.context.getContext().lang) {
      uri += '/lang/' + lang;
    }

    return uri;
  },

  /**
   * Generate link to user avatar
   * @param user
   * @return {String}
   */
  generateAvatarLink: function(user) {
    // If we don't have a user model or that user is a deleted one (Ie. id === 0) then show default hidden image
    if (!user || user.isHidden()) {
      return Config.defaultProfileImage;
    }

    return user.get('imageUrl') || user.get('profileImageSmall') || (Config.cdn.avatars + user.get('userImageHash') + Config.avatars.smallSuffix);
  },

  /**
   * Return the path section of a full healthunlocked.com url for anchors
   * @param {String} url
   * @returns {String}
   */
  getPath: function(url) {
    return url.replace(/^https?\:\/\/healthunlocked\.com/, '');
  },

  /**
   * Generate parameters for url
   * @param {Object} params
   * @returns {String}
   */
  generateParams: function(params) {
    let paramsUrl = Object.keys(params).sort()
      .map(key => `${key}=${encodeURIComponent(params[key])}`)
      .join('&');
    return paramsUrl && `?${paramsUrl}`;
  },

  /**
   * Wrap a function to require login (with optional permissions)
   * - Will route through to login page if login and permission checks fail
   * - Only used for wrapping actions at the moment
   * @param action
   * @param permissions
   * @returns {Function}
   */
  requireLogin: function(action, permissions) {

    return function() {
      var context = this.app.context;
      var isActive = context.isActive();

      if (isActive && context.userHasPermissions(permissions || [])) {
        return action.apply(this, arguments);
      }

      if (isActive) { // Permission failure
        this.app.router.navigate('/'); // TODO: Should show notification and go back to the previous page
        this.app.setAlert(React.createElement(Alert, { children: i18n('You do not have the correct permissions!') }));
        // Not logged in
      } else {
        window.location.replace(`${location.origin}/login${location.pathname}${location.search}${location.hash}`);
      }

      return true;
    };
  },

  /**
   * Generate a callback function which displays an alert
   * @param message
   * @param type
   */
  generateAlert: function(message, type, timeout) {
    return function(status) {
      var alert = React.createElement(Alert, {
        children: message || (status && status.message),
        type: type,
        timeout: timeout,
      });

      this.app.setAlert(alert);
    }.bind(this);
  },

  /**
   * Wrap a promise with an alert, returning a promise which allows the user to continue as normal
   */
  wrapAlert: function(promise, fulfilled, broken, timeout) {
    var fulfilledAlert = this.generateAlert(fulfilled, 'success', timeout);
    var brokenAlert = this.generateAlert(broken, 'danger', timeout);

    return promise.then(fulfilledAlert, brokenAlert);
  },

  /**
   * Scroll the page to a specific referred component
   * @param ref
   */
  scrollToRef: function(ref) {
    if (this.refs[ref] && (!this.scrolled || !this.scrolled[ref])) {
      this.refs[ref].scrollIntoView(true);
      document.body.scrollTop -= Config.scrollPadding;
      if (!this.scrolled) {
        this.scrolled = {};
      }
      this.scrolled[ref] = true;
    }
  },

  /**
   * Prep a link for insertion into anchor href
   * @param link
   * @returns String
   */
  prepLink: function(link) {
    if (link.indexOf('//') > -1) {
      return link;
    }

    return 'http://' + link;
  },

  /**
   * Generates twitter link with username
   * @param username
   * @returns String
   */
  generateTwitterLink: function(username) {
    if (!username) {
      return null;
    }

    return 'https://twitter.com/' + username;
  },

  /**
   * Generates instagram link with username
   * @param username
   * @returns String
   */
  generateInstagramLink: function(username) {
    if (!username) {
      return null;
    }

    return 'https://instagram.com/' + username;
  },

  /**
   * Extracts twitter username from url
   * @param url
   * @returns String
   */
  extractTwitterUsername: function(url) {
    var matches = url.match(/^(https?:\/\/)?(www\.)?twitter\.com\/(#!\/)?@?([^\/]*)/);
    return !matches || matches.length < 4 ? null : matches[4];
  },

  /**
   * Extracts instagram username from url
   * @param url
   * @returns String
   */
  extractInstagramUsername: function(url) {
    var matches = url.match(/^(https?:\/\/)?(www\.)?instagram\.com\/([^\/]*)/);
    return !matches || matches.length < 3 ? null : matches[3];
  },
};



// WEBPACK FOOTER //
// ./app/assets/js/mixins/betelgeuse.js